import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ImageIcon, ChevronRight, Crown, Palette, Zap } from 'lucide-react';
import AccessLevelBadge from './AccessLevelBadge';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const TopDeepWaveImages = ({ limit = 5, showHeader = true, className = "", viewAllPosition = 'top' }) => {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { isAuthenticated, hasActiveSubscription } = useAuth();

    useEffect(() => {
        fetchTopImages();
    }, [limit]);

    const fetchTopImages = async () => {
        try {
            setLoading(true);
            setError(null);

            // Ranked by likes in the last 30 days — the backend falls back to
            // the last 365 days on its own if nothing was liked that recently,
            // so this always reflects real trending activity rather than a
            // frozen all-time total.
            const response = await api.get('/upload/trending/image', {
                params: { userId: 7, limit }
            });

            if (response.data.success) {
                setImages(response.data.data);
            } else {
                console.log('Trending API failed, falling back to legacy method');
                // Fallback to the old method if the new API fails
                await fetchTopImagesLegacy();
            }
        } catch (err) {
            console.error('Error fetching top Deep Wave images:', err);
            console.log('Falling back to legacy method due to error');
            // Fallback to the old method if the new API fails
            await fetchTopImagesLegacy();
        } finally {
            setLoading(false);
        }
    };

    // Fallback method in case the new trending API fails
    const fetchTopImagesLegacy = async () => {
        try {
            // Fetch all public image files and filter for user 7 (The Deep Wave)
            const response = await api.get(`/upload/public/image?limit=50`);
            
            console.log('Legacy upload API response:', response.data);
            
            if (response.data.success) {
                console.log('All images from API:', response.data.data);
                
                // Try a broader search - let's also check for other possible user IDs
                let allImagesForDebugging = response.data.data;
                console.log('Sample of all user IDs in response:', [...new Set(allImagesForDebugging.map(img => img.user_id))]);
                
                // Filter for only The Deep Wave's images - try multiple possible IDs or usernames
                let deepWaveImages = response.data.data.filter(image => {
                    // Check for various possible identifiers for "The Deep Wave"
                    return image.user_id === 7 || 
                           image.username === 'thedeepwave' || 
                           image.username === 'band' ||
                           image.username === 'deepwave' ||
                           image.artist_name === 'The Deep Wave' ||
                           image.display_name === 'The Deep Wave';
                });
                
                console.log('Filtered Deep Wave images (multiple criteria):', deepWaveImages);
                
                // If no specific Deep Wave images found, take the top images overall
                if (deepWaveImages.length === 0) {
                    console.log('No Deep Wave specific images found, using top images overall');
                    deepWaveImages = response.data.data.slice(0, limit);
                }
                
                // Use real play counts with simplified trend calculation
                deepWaveImages = deepWaveImages.map((image, index) => ({
                    ...image,
                    plays: image.play_count || 0,
                    rank: index + 1
                }));
                
                // Sort by like count descending
                deepWaveImages.sort((a, b) => (b.like_count || 0) - (a.like_count || 0));
                
                console.log('Final processed images:', deepWaveImages);
                
                setImages(deepWaveImages.slice(0, limit));
            } else {
                console.log('Legacy API response not successful:', response.data);
                setError('Failed to load top images');
            }
        } catch (err) {
            console.error('Error fetching top Deep Wave images (legacy):', err);
            setError('Unable to load images. Please try again later.');
        }
    };

    const getRankBadgeStyle = (rank) => {
        if (rank === 1) return 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-yellow-900';
        if (rank === 2) return 'bg-gradient-to-r from-gray-400 to-gray-500 text-gray-900';
        if (rank === 3) return 'bg-gradient-to-r from-amber-600 to-amber-700 text-amber-100';
        return 'bg-gradient-to-r from-blue-500 to-blue-700 text-white';
    };

    const formatUploadDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            year: 'numeric'
        });
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '';
        const mb = (bytes / (1024 * 1024)).toFixed(1);
        return `${mb} MB`;
    };

    if (loading) {
        return (
            <div className={`top-deepwave-images ${className}`}>
                {showHeader && (
                    <div className="flex items-center gap-3 mb-6">
                        <ImageIcon className="w-6 h-6 text-blue-400" />
                        <h2 className="text-xl font-bold text-white">Top Artwork by The Deep Wave</h2>
                    </div>
                )}
                
                <div className="space-y-3">
                    {[...Array(limit)].map((_, index) => (
                        <div key={index} className="flex items-center gap-4 bg-gray-800/30 rounded-lg p-3 border border-gray-700/50 animate-pulse">
                            <div className="w-8 h-8 bg-gray-700 rounded-lg"></div>
                            <div className="w-12 h-12 bg-gray-700 rounded-lg"></div>
                            <div className="flex-1">
                                <div className="h-4 bg-gray-700 rounded w-3/4 mb-2"></div>
                                <div className="h-3 bg-gray-700 rounded w-1/2"></div>
                            </div>
                            <div className="w-12 h-4 bg-gray-700 rounded"></div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={`top-deepwave-images ${className}`}>
                {showHeader && (
                    <div className="flex items-center gap-3 mb-6">
                        <ImageIcon className="w-6 h-6 text-blue-400" />
                        <h2 className="text-xl font-bold text-white">Top Artwork by The Deep Wave</h2>
                    </div>
                )}
                
                <div className="text-center py-8">
                    <div className="text-4xl mb-2">🎨</div>
                    <p className="text-gray-400">{error}</p>
                </div>
            </div>
        );
    }

    if (images.length === 0) {
        return (
            <div className={`top-deepwave-images ${className}`}>
                {showHeader && (
                    <div className="flex items-center gap-3 mb-6">
                        <ImageIcon className="w-6 h-6 text-blue-400" />
                        <h2 className="text-xl font-bold text-white">Top Artwork by The Deep Wave</h2>
                    </div>
                )}
                
                <div className="text-center py-8">
                    <div className="text-4xl mb-2">🖼️</div>
                    <p className="text-gray-400 mb-4">No artwork available yet</p>
                    <p className="text-sm text-gray-500">Check back soon for top artwork!</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`top-deepwave-images ${className}`}>
            {showHeader && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3 sm:gap-0">
                    <div className="flex items-center gap-3">
                        <ImageIcon className="w-6 h-6 text-blue-400" />
                        <h2 className="text-xl font-bold text-white">Top Artwork by The Deep Wave</h2>
                        <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded-full flex items-center gap-1">
                            <Zap className="w-3 h-3" />
                            Trending
                        </span>
                    </div>
                    {viewAllPosition === 'top' && (
                        <Link
                            to="/feed/artist/TheDeepWave/images?sort=views"
                            className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1 transition-colors self-start sm:self-auto"
                        >
                            View All
                            <ChevronRight className="w-4 h-4" />
                        </Link>
                    )}
                </div>
            )}
            
            <div className="space-y-3">
                {images.map((image, index) => (
                    <Link
                        key={image.id}
                        to={`/view/image/${image.username || 'band'}/${image.stored_filename || image.original_filename || image.id}`}
                        className="group flex items-center gap-2 sm:gap-4 bg-gray-800/30 hover:bg-gray-800/50 rounded-lg p-2 sm:p-3 border border-gray-700/50 hover:border-blue-500/30 transition-all duration-300"
                    >
                        {/* Rank Badge */}
                        <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${getRankBadgeStyle(index + 1)}`}>
                            {index + 1 === 1 && <Crown className="w-3 h-3 sm:w-4 sm:h-4" />}
                            {index + 1 !== 1 && <span className="font-bold text-xs sm:text-sm">{index + 1}</span>}
                        </div>

                        {/* Image Thumbnail */}
                        <div className="relative w-10 h-10 sm:w-12 sm:h-12 rounded-lg overflow-hidden flex-shrink-0 bg-gray-900">
                            <img
                                src={image.optimized_url || image.file_url}
                                alt={image.title || 'Artwork'}
                                width="40"
                                height="40"
                                className="w-full h-full object-cover"
                                onContextMenu={(e) => e.preventDefault()}
                                onDragStart={(e) => e.preventDefault()}
                            />
                            <AccessLevelBadge accessLevel={image.access_level} iconOnly isAuthenticated={isAuthenticated} hasActiveSubscription={hasActiveSubscription} />
                        </div>

                        {/* Image Info */}
                        <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                    <h3 className="font-semibold text-white truncate group-hover:text-blue-300 transition-colors text-sm sm:text-base">
                                        {image.title || `Artwork ${index + 1}`}
                                    </h3>
                                    <p className="text-xs sm:text-sm text-gray-400 flex items-center gap-1 sm:gap-2 flex-wrap">
                                        <span className="whitespace-nowrap">{(image.like_count || 0).toLocaleString()} likes</span>
                                        <span className="hidden sm:inline">•</span>
                                        <span className="whitespace-nowrap hidden sm:inline">{formatUploadDate(image.created_at)}</span>
                                        <span className="hidden sm:inline">•</span>
                                        <span className="whitespace-nowrap text-xs hidden sm:inline">{formatFileSize(image.file_size)}</span>
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Popularity Indicator */}
                        <div className="hidden sm:block">
                            <div className="flex items-center gap-1">
                                {[...Array(5)].map((_, i) => {
                                    // Calculate popularity based on like count - adjust the max value based on your typical like counts
                                    const maxLikes = Math.max(100, images[0]?.like_count || 100);
                                    const popularityLevel = Math.floor(((image.like_count || 0) / maxLikes) * 5);
                                    
                                    return (
                                        <div
                                            key={i}
                                            className={`w-1 rounded-full transition-all duration-300 ${
                                                i < popularityLevel 
                                                    ? 'bg-red-400 h-4' 
                                                    : 'bg-gray-600 h-2'
                                            }`}
                                        ></div>
                                    );
                                })}
                            </div>
                        </div>
                    </Link>
                ))}
            </div>

            {viewAllPosition === 'bottom' ? (
                <div className="mt-6 text-center">
                    <Link
                        to="/feed/artist/TheDeepWave/images?sort=views"
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600/10 hover:bg-blue-600/20 text-blue-400 hover:text-blue-300 rounded-lg transition-all duration-200 hover:scale-105 text-sm border border-blue-600/20"
                    >
                        <Palette className="w-3.5 h-3.5" />
                        View All Top Artwork
                    </Link>
                </div>
            ) : images.length >= limit && (
                <div className="mt-4 text-center">
                    <Link
                        to="/feed/artist/TheDeepWave/images?sort=views"
                        className="inline-flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 transition-colors"
                    >
                        <Palette className="w-4 h-4" />
                        View Full Top Artwork Charts
                        <ChevronRight className="w-4 h-4" />
                    </Link>
                </div>
            )}
        </div>
    );
};

export default TopDeepWaveImages;